import { useEffect, useState } from 'react';

import * as canvasApi from '@/features/canvas/api';
import { cn } from '@/components/ui/cn';

/**
 * A whiteboard's preview thumbnail — a server-rendered SVG, auth-fetched (the snapshot stream 401s a
 * bare <img>). The backend renders one on first read if none exists yet, so we always attempt the fetch
 * and fall back to a canvas glyph only if it genuinely fails. `snapshotAt` (when known) cache-busts.
 */
export function WhiteboardThumb({ whiteboardId, snapshotAt, className }: { whiteboardId: string; snapshotKey?: string | null; snapshotAt?: string | null; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let obj: string | null = null;
    setFailed(false);
    canvasApi
      .fetchSnapshotBlob(whiteboardId)
      .then((blob) => {
        if (cancelled) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [whiteboardId, snapshotAt]);

  return (
    <div className={cn('overflow-hidden bg-surface-inset', className)}>
      {url && !failed ? (
        <img src={url} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-fg-ghost">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <rect x="6" y="7" width="5" height="4" rx="1" fill="currentColor" opacity="0.5" />
            <circle cx="16" cy="9" r="2" stroke="currentColor" strokeWidth="1.3" />
            <path d="M7 14h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  );
}
